"use client";
import React, { useCallback, useEffect, useState } from "react";
import Image from "next/image";
import CustomRangeBar from "@/components/ui/CustomRangeBar";
import { calculateSipStepUp } from "@/utils/calculateSipTopUp"; 
import { Scheme } from "@/type/schemeType";
import { useIsMobile } from "@/utils/useIsMobile";
import { useDispatch } from "react-redux";
import { openInvestmentModal } from "@/redux/features/InvestmentModal/investmentModalSlice";
import { SchemeOptional } from "@/type/commonTypes";
import { ROOT_URL } from "@/utils/gloableVariables";

interface GrowthCalcStripProps {
  schemeData: Scheme;
}

type StepupSummary = {
  totalInvestment: number;
  totalCorpus: number;
  totalReturns: number;
  cagr: number;
};

export default function GrowthCalcStrip({ schemeData }: GrowthCalcStripProps) {
  const [investmentType, setInvestmentType] = useState<"SIP" | "Lumpsum">("SIP");
  const [amount, setAmount] = useState("100");
  const [showError, setShowError] = useState(false);
  const [selectedYears, setSelectedYears] = useState<number>(1);
  const [returns, setReturns] = useState(5);

  const investmentAllow = !!(schemeData.sip_allowed || schemeData.lump_allowed);
  const isMobile = useIsMobile(631);

  const dispatch = useDispatch();

  const [errorMessage, setErrorMessage] = useState("");
  const [summary, setSummary] = useState<StepupSummary | null>(null);

  useEffect(() => {
    setAmount(investmentType === "SIP" ? "100" : "10000");
    setShowError(false);
  }, [investmentType]);

  const MAX_INVESTMENT_AMOUNT = investmentType === "SIP" ? 100000 : 1000000;

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value.replace(/[^0-9]/g, "");
    if (raw.length > 1) raw = raw.replace(/^0+/, "");
    if (raw === "") {
      setAmount("0");
      setShowError(true);
      return;
    }
    const num = Number(raw);
    if (num > MAX_INVESTMENT_AMOUNT) {
      setAmount(MAX_INVESTMENT_AMOUNT.toLocaleString("en-IN"));
      setShowError(true);
      setErrorMessage(`Maximum investment allowed is ₹${MAX_INVESTMENT_AMOUNT.toLocaleString("en-IN")}.`);
      return;
    }
    const formatted = num.toLocaleString("en-IN");
    setAmount(formatted);
    if (num < 100) {
      setShowError(true);
      setErrorMessage("Minimum investment amount is ₹100.");
    } else {
      setShowError(false);
      setErrorMessage("");
    }
  };

  const stepupCalculation = useCallback(async () => {
    const investAmountNum = Number(amount.replace(/,/g, ""));
    const increaseBy = returns;

    const selectedReturnRate = !useFallback ? returnOptions.find((opt) => opt.label === selectedYears)?.value || 0 : 12;

    if (investmentType === "SIP") {
      const sipResult = calculateSipStepUp({
        amount,
        expectedReturn: selectedReturnRate,
        years: selectedYears,
        increaseBy,
      });

      return sipResult;
    } else {
      const investAmount = investAmountNum;

      const totalCorpus = investAmount * Math.pow(1 + selectedReturnRate / 100, selectedYears);

      const totalReturns = totalCorpus - investAmount;
      const cagr = investAmount > 0 ? (Math.pow(totalCorpus / investAmount, 1 / selectedYears) - 1) * 100 : 0;

      return {
        totalInvestment: Math.round(investAmount),
        totalCorpus: Math.round(totalCorpus),
        totalReturns: Math.round(totalReturns),
        cagr: Number(cagr.toFixed(2)), // keep it numeric
      };
    }
  }, [amount, returns, selectedYears, investmentType]);

  const formatCurrency = (value: number): string => {
    return value.toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    });
  };

  useEffect(() => {
    const run = async () => {
      const res = await stepupCalculation();
      setSummary(res);
    };
    run();
  }, [stepupCalculation]);

  const returnOptions = [
    { label: 1, value: schemeData.sr_one_year_abs || 0, key: "sr_one_year_abs" },
    { label: 3, value: schemeData.sr_three_year || 0, key: "sr_three_year" },
    { label: 5, value: schemeData.sr_five_year || 0, key: "sr_five_year" },
  ];

  const validReturnOptions = returnOptions.filter((opt) => opt.value !== null && opt.value !== undefined && !isNaN(Number(opt.value)) && Number(opt.value) !== 0);

  const useFallback = validReturnOptions.length === 0;

  const summaryData = summary
    ? [
        {
          label: "Total Investment",
          value: `${formatCurrency(Number(summary.totalInvestment))}`,
          color: "text-black",
        },
        {
          label: "Total Corpus",
          value: `${formatCurrency(Number(summary.totalCorpus))}`,
          color: Number(summary.totalCorpus) >= 0 ? "text-primaryBlue" : "text-[#ec4d4d]",
        },
        {
          label: "CAGR",
          value: `${Number(summary.cagr).toFixed(2)}%`,
          color: Number(summary.cagr) >= 0 ? "text-[#41BC2E]" : "text-[#ec4d4d]",
        },
      ]
    : [];

  useEffect(() => {
    if (useFallback) {
      setSelectedYears(5);
    } else {
      setSelectedYears(validReturnOptions[0].label);
    }
  }, [useFallback]);

  const handleSelect = (rt: number) => {
    setSelectedYears(rt);
  };

  const handleTypeSelection = (type: "SIP" | "Lumpsum") => {
    setInvestmentType(type);
    setAmount("100");
  };

  const formPlayload: SchemeOptional = {
    scheme_code: schemeData?.scheme_code || "",
    scheme_name: schemeData?.scheme_name || "",
    amc_code: schemeData?.amc_code || "",
    sip_allowed: schemeData.sip_allowed,
    lump_allowed: schemeData.lump_allowed,
    minimum_sip_invest: schemeData.minimum_sip_invest,
    minimum_investment: schemeData.minimum_investment,
  };

  return (
    <div className="border border-[#E9E9EB] bg-white rounded-4xl p-5 xs:p-7">
      <div className="flex items-center gap-3 mb-4">
        <Image src={`${ROOT_URL}img/AMCsLogo/${schemeData.amc_code}.png`} alt={schemeData?.scheme_name || "Fund Icon"} width={40} height={40} className="w-9 h-9 sm:w-10 sm:h-10 overflow-hidden shrink-0 border border-[#E9E9EB] rounded-full" />

        <p className="font-semibold text-[17px] sm:text-[19px] text-primaryBlack leading-5.5 line-clamp-2">
          {schemeData.scheme_name} <br />
          <span className="block font-normal text-2sm leading-4 sm:text-base sm:leading-6 text-primaryBlack mt-2">Growth Calculator</span>
        </p>
      </div>

      <div className="flex items-center justify-between gap-2 p-1 rounded-[100px] border border-[#1A51AA24] shadow-[0px_4px_10px_0px_#0000000D] overflow-hidden bg-[#EBF3FF] mb-4">
        {(["SIP", "Lumpsum"] as const).map((type) => (
          <label key={type} className={`group capitalize w-full px-4 py-1 sm:py-3 rounded-[100px] transition-all duration-350 ease-in-out flex-1 cursor-pointer ${investmentType === type ? "bg-white" : null}`}>
            <input type="radio" name="investmentType" checked={investmentType === type} onChange={() => handleTypeSelection(type)} className="hidden" />
            <p className={`text-sm text-center ${investmentType === type ? "text-primaryBlue font-bold" : "text-primaryBlack group-hover:text-primaryBlue group-hover:font-medium"}`}>{type === "SIP" ? "Monthly SIP" : "One Time"}</p>
          </label>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-7">
        <div className="flex-1">
          <div className={`flex gap-4 flex-col ${showError ? "mb-1" : "mb-4"}`}>
            <div className="flex-1">
              <p className="text-sm mb-1.5 text-[#444444]">{investmentType === "SIP" ? "SIP" : "Lumpsum"} Amount</p>

              <label className="block relative">
                <span className={`absolute left-4 top-1/2 -translate-y-1/2 text-sm ${showError ? "text-[#ec4d4d]" : "text-black"}`}>₹</span>
                <input type="text" value={amount} onChange={handleAmountChange} className={`border text-sm font-medium w-full py-2.5 px-6 rounded-xl bg-white transition-all duration-200 ease-in-out ${showError ? "border-[#ec4d4d] text-[#ec4d4d] " : "border-[#1A51AA33] hover:border-[#1a51aa66] text-black "}`} />
              </label>
            </div>

            <div className="flex-1">
              <p className="text-sm mb-1.5 text-[#444444]">Investment Duration</p>
              <div className="flex items-center gap-3 sm:gap-2 md:gap-4">
                {!useFallback ? (
                  validReturnOptions.map((opt) => (
                    <button key={opt.label} onClick={() => handleSelect(opt.label)} className={`rounded-full w-full max-w-44 px-4 py-2 text-sm font-medium transition-colors duration-200 ${selectedYears === opt.label ? "bg-primaryBlue text-white" : "bg-white text-black border border-[#e4e4e4] hover:border-primaryBlue"}`}>
                      {opt.label} {opt.label === 1 ? (isMobile ? "Yr" : "Year") : isMobile ? "Yrs" : "Years"}
                    </button>
                  ))
                ) : (
                  <>
                    <p className="font-medium text-sm sm:text-base text-primaryBlue">Estimated average annual return of 12% over 5 years.</p>
                  </>
                )}
              </div>
            </div>
          </div>

          {showError && <p className="text-2xs text-[#ec4d4d] mb-4">{errorMessage}</p>}

          {investmentType === "SIP" && (
            <div className={`xs:mb-4 ${selectedYears === 1 ? "opacity-60" : ""}`} title={selectedYears === 1 ? "Step-up applies only when the investment period is more than one year." : ""}>
              <CustomRangeBar label="Step up (Annually)" labelStyle={`text-[#444444] text-sm mb-2.5`} value={selectedYears === 1 ? 0 : returns} min={0} max={50} unit="%" onChange={setReturns} disabled={selectedYears === 1} />
            </div>
          )}
        </div>

        <div className="flex-1">
          <div className="bg-[#F5F9FF] rounded-xl py-3 px-5 w-full max-w-sm mb-4 sm:mb-7 mx-auto">
            {summaryData.map(({ label, value, color }, index) => (
              <div key={label} className={`flex justify-between ${index !== summaryData.length - 1 ? "mb-2.5 xs:mb-7" : null}`}>
                <p className="text-[#444444] text-sm">{label}</p>
                <p className={`font-medium text-base ${color}`}>{value}</p>
              </div>
            ))}
          </div>

          {!investmentAllow ? (
            <button title={"This fund is suspanded, can't invest. Try another"} disabled={true} className={`block w-full border border-[#1A51AA99] py-2 xs:py-3 text-center rounded-xl flex-1 cursor-pointer text-white bg-primaryBlue opacity-50`}>
              Invest in this fund
            </button>
          ) : (
            <button onClick={() => dispatch(openInvestmentModal(formPlayload))} className="block w-full border border-[#1A51AA99] py-2 xs:py-3 text-center rounded-xl flex-1 cursor-pointer text-white bg-primaryBlue shine-button-hover">
              Invest Now
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
